import { useEffect, useMemo, useState } from "react";

import { useGlobalContext } from "./use-global-context";

export const CERTIFICATION_TIMER_MS = 60 * 60 * 1000;

export const useOnboarding = () => {
  const { glencocoUser: user } = useGlobalContext();
  const { user_onboarding_status } = user || {};
  const { certification_started_at, picked_campaign } =
    user_onboarding_status || {};

  const startedAtMs = useMemo(() => {
    if (!certification_started_at) {
      return null;
    }

    const ms = new Date(certification_started_at).getTime();

    return Number.isNaN(ms) ? null : ms;
  }, [certification_started_at]);

  // Tick to force a re-derivation of `isCertified` once the 1-hour timer
  // elapses, so the second onboarding card auto-unlocks without needing
  // the user to refresh the page.
  const [tickAt, setTickAt] = useState(0);
  useEffect(() => {
    if (startedAtMs === null) {
      return;
    }

    const remaining = startedAtMs + CERTIFICATION_TIMER_MS - Date.now();
    if (remaining <= 0) {
      return;
    }

    const t = setTimeout(() => setTickAt(Date.now()), remaining);

    return () => clearTimeout(t);
  }, [startedAtMs]);

  const isCertified = useMemo(() => {
    if (startedAtMs === null) {
      return false;
    }

    return Date.now() - startedAtMs >= CERTIFICATION_TIMER_MS;
  }, [startedAtMs, tickAt]);

  const step = useMemo(() => {
    return [isCertified, picked_campaign].reduce(
      (acc: number, cur: any) => acc + (cur ? 1 : 0),
      0
    );
  }, [isCertified, picked_campaign]);

  const isOnboarded = useMemo(() => {
    return [isCertified, picked_campaign].every((check) => !!check);
  }, [isCertified, picked_campaign]);

  return { step, isOnboarded, isCertified };
};
